import { useState, useMemo, useRef, lazy, Suspense, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import toolsData from "@/data/toolsData.json";
import type { Tool, FilterCategory, SortOption } from "@/types/tool";
import Hero from "@/components/Hero";
import SocialProof from "@/components/SocialProof";
import QuickMatchBanner from "@/components/QuickMatchBanner";
import InlineNewsletter from "@/components/InlineNewsletter";
import FilterTabs from "@/components/FilterTabs";
import ToolsGrid from "@/components/ToolsGrid";
import ToolsGridSkeleton from "@/components/ToolsGridSkeleton";
import FloatingSelectionBar from "@/components/FloatingSelectionBar";
import BackgroundDecoration from "@/components/BackgroundDecoration";
import Header from "@/components/Header";
import QuickMatchModal from "@/components/QuickMatchModal";
import ToolNameSearch from "@/components/ToolNameSearch";
import { applySeo } from "@/lib/seo";


const Newsletter = lazy(() => import("@/components/Newsletter"));
const NewsTeaser = lazy(() => import("@/components/NewsTeaser"));
const Footer = lazy(() => import("@/components/Footer"));
import { usePromotedTools } from "@/hooks/usePromotedTools";

const ComparisonModal = lazy(() => import("@/components/ComparisonModal"));

const TOOLS_PER_PAGE = 10;

const Index = () => {
  const location = useLocation();
  const [filter, setFilter] = useState<FilterCategory>("all");
  const [priceFilter, setPriceFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isCompareOpen, setIsCompareOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOption, setSortOption] = useState<SortOption>("price-asc");
  const [isGridLoaded, setIsGridLoaded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(TOOLS_PER_PAGE);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [quickMatchOpen, setQuickMatchOpen] = useState(false);
  const toolsGridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setHeaderVisible(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const { data: promotedToolIds = [] } = usePromotedTools();
  const tools = toolsData as Tool[];

  useEffect(() => {
    const timer = setTimeout(() => setIsGridLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    return applySeo({
      title: "Stackly — Compare AI Tools Side by Side",
      description: "Find and compare 100+ AI tools for building, coding, and creating. Rated by autonomy, vibe score, and price. Free forever.",
      path: "/",
      jsonLdId: "home-jsonld",
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Stackly — AI Tools Library",
        url: "https://getstackly.tech/",
        description: "Curated collection of 100+ AI tools with side-by-side comparisons.",
        mainEntity: {
          "@type": "ItemList",
          numberOfItems: tools.length,
          itemListElement: tools.slice(0, 20).map((t, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://getstackly.tech/tool/${t.id}`,
            name: t.name,
          })),
        },
      },
    });
  }, [tools]);

  useEffect(() => {
    const state = location.state as { compareToolId?: string } | null;
    if (state?.compareToolId) {
      setSelectedIds((prev) =>
        prev.includes(state.compareToolId!) ? prev : [...prev.slice(0, 3), state.compareToolId!]
      );
      window.history.replaceState({}, document.title);
      setTimeout(() => {
        toolsGridRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [location.state]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const compareParam = params.get("compare");
    if (compareParam) {
      const ids = compareParam.split(",").filter(Boolean);
      const validIds = ids.filter(id => tools.some(t => t.id === id));
      if (validIds.length >= 2) {
        setSelectedIds(validIds.slice(0, 4));
        setIsCompareOpen(true);
      }
    }
  }, [location.search, tools]);

  const filteredTools = useMemo(() => {
    return tools
      .filter((tool) => {
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          const matchesSearch =
            tool.name.toLowerCase().includes(q) ||
            tool.description.toLowerCase().includes(q) ||
            tool.bestFor.toLowerCase().includes(q) ||
            tool.features.some((f) => f.toLowerCase().includes(q));
          if (!matchesSearch) return false;
        }
        if (filter !== "all" && tool.category !== filter) return false;
        if (priceFilter === "free" && tool.price !== 0) return false;
        if (priceFilter === "budget" && (tool.price === 0 || tool.price > 20)) return false;
        if (priceFilter === "premium" && tool.price < 20) return false;
        return true;
      })
      .sort((a, b) => {
        const aP = promotedToolIds.includes(a.id);
        const bP = promotedToolIds.includes(b.id);
        if (aP && !bP) return -1;
        if (!aP && bP) return 1;
        switch (sortOption) {
          case "price-asc": return a.price - b.price;
          case "price-desc": return b.price - a.price;
          case "autonomy-desc": return b.autonomy - a.autonomy;
          case "name-asc": return a.name.localeCompare(b.name);
          default: return a.price - b.price;
        }
      });
  }, [tools, filter, priceFilter, searchQuery, sortOption, promotedToolIds]);

  useEffect(() => {
    setVisibleCount(TOOLS_PER_PAGE);
  }, [filter, priceFilter, searchQuery, sortOption]);

  const selectedTools = useMemo(() => 
    tools.filter((tool) => selectedIds.includes(tool.id)),
    [tools, selectedIds]
  );

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) 
        ? prev.filter((i) => i !== id)
        : prev.length < 4 
          ? [...prev, id]
          : prev
    );
  };

  const handleRemove = (id: string) => {
    setSelectedIds((prev) => prev.filter((i) => i !== id));
  };

  const handleClear = () => setSelectedIds([]);

  const handleCompare = () => {
    if (selectedTools.length >= 2) setIsCompareOpen(true);
  };

  const handleToolFinderSelect = (ids: string[]) => {
    setSelectedIds(ids);
    setIsCompareOpen(true);
    setTimeout(() => {
      toolsGridRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const openQuickMatch = () => setQuickMatchOpen(true);

  const activeFiltersCount = [priceFilter !== "all", searchQuery.length > 0].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-background relative">
      <Header />
      <BackgroundDecoration />
      
      <Hero onQuickMatch={openQuickMatch} />

      <QuickMatchModal
        open={quickMatchOpen}
        onOpenChange={setQuickMatchOpen}
        tools={tools}
        onSelectTools={handleToolFinderSelect}
      />

      {/* Sticky filter bar */}
      <div id="tools" ref={toolsGridRef} className={`sticky z-40 bg-background/95 backdrop-blur-md border-b border-border/50 transition-[top] duration-300 ${headerVisible ? 'top-[52px]' : 'top-0'}`}>
        <div className="max-w-4xl mx-auto px-4 pt-3 pb-2">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-3">
              <h2 className="text-base font-bold text-foreground">All Tools</h2>
              <span className="text-xs text-muted-foreground">{filteredTools.length} results</span>
            </div>
            {activeFiltersCount > 0 && (
              <button
                onClick={() => { setPriceFilter("all"); setSearchQuery(""); }}
                className="text-[11px] text-muted-foreground hover:text-foreground transition-colors underline"
              >
                Clear
              </button>
            )}
          </div>
          <div className="mb-2">
            <ToolNameSearch tools={tools} onSelect={() => { /* scroll handled inside */ }} />
          </div>
          <FilterTabs 
            activeFilter={filter} 
            onFilterChange={setFilter}
            priceFilter={priceFilter}
            onPriceFilterChange={setPriceFilter}
            tools={tools}
            sortOption={sortOption}
            onSortChange={setSortOption}
          />
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 pt-4 pb-12">
        {isGridLoaded ? (
          <>
            <ToolsGrid
              tools={filteredTools.slice(0, visibleCount)}
              filter="all"
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              promotedToolIds={promotedToolIds}
            />
            {visibleCount < filteredTools.length && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={() => setVisibleCount((prev) => prev + TOOLS_PER_PAGE)}
                  className="px-6 py-2.5 rounded-full bg-secondary border border-border text-foreground text-sm font-medium hover:bg-secondary/80 transition-colors"
                >
                  Load more tools
                </button>
              </div>
            )}
          </>
        ) : (
          <ToolsGridSkeleton />
        )}

        {filteredTools.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-sm">No tools match your filters.</p>
            <button
              onClick={() => { setFilter("all"); setPriceFilter("all"); setSearchQuery(""); }}
              className="mt-2 text-xs text-foreground underline"
            >
              Reset all filters
            </button>
          </div>
        )}
      </main>

      <Suspense fallback={<div className="py-8" />}>
        <NewsTeaser />
      </Suspense>

      <div id="newsletter">
        <Suspense fallback={<div className="py-8" />}>
          <Newsletter />
        </Suspense>
      </div>

      <Suspense fallback={null}>
        <Footer />
      </Suspense>

      <FloatingSelectionBar
        selectedTools={selectedTools}
        onRemove={handleRemove}
        onCompare={handleCompare}
        onClear={handleClear}
      />

      {isCompareOpen && (
        <Suspense fallback={null}>
          <ComparisonModal
            isOpen={isCompareOpen}
            onClose={() => setIsCompareOpen(false)}
            tools={selectedTools}
          />
        </Suspense>
      )}
    </div>
  );
};

export default Index;
